import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  envelopeApi,
  templateApi,
  folderApi,
  tagApi,
  usageApi,
  adminApi,
  teamApi,
  webhooksApi,
  apiKeysApi,
  analyticsApi,
  signingLinksApi,
  clauseApi,
  getErrorMessage,
} from "@/services/api";

// =============================================================================
// QUERY KEYS - Centralized key management for cache invalidation
// =============================================================================

export const queryKeys = {
  // Envelopes
  envelopes: {
    all: ["envelopes"],
    list: (filters) => ["envelopes", "list", filters],
    detail: (id) => ["envelopes", "detail", id],
    recipients: (id) => ["envelopes", "recipients", id],
    activity: (id) => ["envelopes", "activity", id],
    certificate: (id) => ["envelopes", "certificate", id],
  },

  // Templates
  templates: {
    all: ["templates"],
    list: (filters) => ["templates", "list", filters],
    detail: (id) => ["templates", "detail", id],
    categories: ["templates", "categories"],
  },

  // Folders
  folders: {
    all: ["folders"],
    list: () => ["folders", "list"],
    detail: (id) => ["folders", "detail", id],
  },

  // Tags
  tags: {
    all: ["tags"],
    list: () => ["tags", "list"],
  },

  // Usage & Analytics
  usage: {
    current: ["usage", "current"],
    history: (period) => ["usage", "history", period],
  },
  analytics: {
    dashboard: (period) => ["analytics", "dashboard", period],
    envelopes: (params) => ["analytics", "envelopes", params],
  },

  // Team
  team: {
    members: ["team", "members"],
    invitations: ["team", "invitations"],
  },

  // Admin
  admin: {
    dashboard: ["admin", "dashboard"],
    users: (filters) => ["admin", "users", filters],
    userDetail: (id) => ["admin", "users", "detail", id],
    organisations: (filters) => ["admin", "organisations", filters],
  },

  // Webhooks
  webhooks: {
    all: ["webhooks"],
    list: () => ["webhooks", "list"],
    detail: (id) => ["webhooks", "detail", id],
    logs: (id) => ["webhooks", "logs", id],
  },

  // API Keys
  apiKeys: {
    all: ["apiKeys"],
    list: () => ["apiKeys", "list"],
  },

  // Signing Links
  signingLinks: {
    all: ["signingLinks"],
    list: (filters) => ["signingLinks", "list", filters],
    detail: (id) => ["signingLinks", "detail", id],
  },

  // Clauses
  clauses: {
    all: ["clauses"],
    list: (filters) => ["clauses", "list", filters],
    detail: (id) => ["clauses", "detail", id],
    categories: ["clauses", "categories"],
  },
};

// =============================================================================
// ENVELOPE HOOKS
// =============================================================================

/**
 * Fetch paginated list of envelopes
 */
export const useEnvelopes = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.envelopes.list(filters),
    queryFn: async () => {
      const response = await envelopeApi.list(filters);
      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });

/**
 * Fetch single envelope details
 */
export const useEnvelope = (id, options = {}) =>
  useQuery({
    queryKey: queryKeys.envelopes.detail(id),
    queryFn: async () => {
      const response = await envelopeApi.get(id);
      return response.data.data;
    },
    enabled: !!id,
    ...options,
  });

/**
 * Fetch envelope recipients
 */
export const useEnvelopeRecipients = (envelopeId) =>
  useQuery({
    queryKey: queryKeys.envelopes.recipients(envelopeId),
    queryFn: async () => {
      const response = await envelopeApi.getRecipients(envelopeId);
      return response.data.data;
    },
    enabled: !!envelopeId,
  });

/**
 * Fetch envelope activity/audit trail
 */
export const useEnvelopeActivity = (envelopeId) =>
  useQuery({
    queryKey: queryKeys.envelopes.activity(envelopeId),
    queryFn: async () => {
      const response = await envelopeApi.getActivity(envelopeId);
      return response.data.data;
    },
    enabled: !!envelopeId,
  });

/**
 * Create envelope mutation
 */
export const useCreateEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await envelopeApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });
      toast.success("Envelope created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create envelope"));
    },
  });
};

/**
 * Update envelope mutation
 */
export const useUpdateEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await envelopeApi.update(id, data);
      return response.data.data;
    },
    onSuccess: (data, { id, data: updateData }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.envelopes.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });

      // If folder was updated, invalidate folders cache to update envelope counts
      if ("folderId" in updateData) {
        queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update envelope"));
    },
  });
};

/**
 * Delete envelope mutation
 */
export const useDeleteEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await envelopeApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });
      toast.success("Envelope deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete envelope"));
    },
  });
};

/**
 * Send envelope mutation
 */
export const useSendEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await envelopeApi.send(id);
      return response.data.data;
    },
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.envelopes.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });
      toast.success("Envelope sent successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to send envelope"));
    },
  });
};

/**
 * Void envelope mutation
 */
export const useVoidEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const response = await envelopeApi.void(id, reason);
      return response.data.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.envelopes.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });
      toast.success("Envelope voided successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to void envelope"));
    },
  });
};

/**
 * Duplicate envelope mutation
 */
export const useDuplicateEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await envelopeApi.duplicate(id);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.envelopes.all });
      toast.success("Envelope duplicated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate envelope"));
    },
  });
};

// =============================================================================
// TEMPLATE HOOKS
// =============================================================================

/**
 * Fetch paginated list of templates
 */
export const useTemplates = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.templates.list(filters),
    queryFn: async () => {
      const response = await templateApi.list(filters);
      return response.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch single template details
 */
export const useTemplate = (id, options = {}) =>
  useQuery({
    queryKey: queryKeys.templates.detail(id),
    queryFn: async () => {
      const response = await templateApi.get(id);
      return response.data.data;
    },
    enabled: !!id,
    ...options,
  });

/**
 * Fetch template categories
 */
export const useTemplateCategories = () =>
  useQuery({
    queryKey: queryKeys.templates.categories,
    queryFn: async () => {
      const response = await templateApi.getCategories();
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes (categories rarely change)
  });

/**
 * Create template mutation
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData) => {
      const response = await templateApi.create(formData);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      toast.success("Template created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create template"));
    },
  });
};

/**
 * Update template mutation
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await templateApi.update(id, data);
      return response.data.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.templates.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      toast.success("Template saved successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update template"));
    },
  });
};

/**
 * Delete template mutation
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await templateApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      toast.success("Template deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete template"));
    },
  });
};

/**
 * Duplicate template mutation
 */
export const useDuplicateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await templateApi.duplicate(id);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.templates.all });
      toast.success("Template duplicated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to duplicate template"));
    },
  });
};

// =============================================================================
// FOLDER HOOKS
// =============================================================================

/**
 * Fetch all folders
 * Pass enabled: false to disable the query (e.g., when user lacks feature access)
 */
export const useFolders = (options = {}) =>
  useQuery({
    queryKey: queryKeys.folders.list(),
    queryFn: async () => {
      const response = await folderApi.getAll();
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options.enabled !== false, // Allow caller to disable
    ...options,
  });

/**
 * Create folder mutation
 */
export const useCreateFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await folderApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
      toast.success("Folder created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create folder"));
    },
  });
};

/**
 * Update folder mutation
 */
export const useUpdateFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await folderApi.update(id, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
      toast.success("Folder updated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update folder"));
    },
  });
};

/**
 * Delete folder mutation
 */
export const useDeleteFolder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await folderApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.folders.all });
      toast.success("Folder deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete folder"));
    },
  });
};

// =============================================================================
// TAG HOOKS
// =============================================================================

/**
 * Fetch all tags
 * Pass enabled: false to disable the query (e.g., when user lacks feature access)
 */
export const useTags = (options = {}) =>
  useQuery({
    queryKey: queryKeys.tags.list(),
    queryFn: async () => {
      const response = await tagApi.getAll();
      return response.data.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options.enabled !== false, // Allow caller to disable
    ...options,
  });

/**
 * Create tag mutation
 */
export const useCreateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await tagApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
      toast.success("Tag created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create tag"));
    },
  });
};

/**
 * Delete tag mutation
 */
export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await tagApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
      toast.success("Tag deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete tag"));
    },
  });
};

/**
 * Add tag to envelope
 */
export const useAddTagToEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ envelopeId, tagId }) => {
      await tagApi.addToEnvelope(envelopeId, tagId);
      return { envelopeId, tagId };
    },
    onSuccess: ({ envelopeId }) => {
      // Invalidate tags list to update envelope counts
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
      // Invalidate the specific envelope
      queryClient.invalidateQueries({
        queryKey: queryKeys.envelopes.detail(envelopeId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to add tag"));
    },
  });
};

/**
 * Remove tag from envelope
 */
export const useRemoveTagFromEnvelope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ envelopeId, tagId }) => {
      await tagApi.removeFromEnvelope(envelopeId, tagId);
      return { envelopeId, tagId };
    },
    onSuccess: ({ envelopeId }) => {
      // Invalidate tags list to update envelope counts
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
      // Invalidate the specific envelope
      queryClient.invalidateQueries({
        queryKey: queryKeys.envelopes.detail(envelopeId),
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to remove tag"));
    },
  });
};

// =============================================================================
// USAGE & ANALYTICS HOOKS
// =============================================================================

/**
 * Fetch current usage stats
 */
export const useUsage = () =>
  useQuery({
    queryKey: queryKeys.usage.current,
    queryFn: async () => {
      const response = await usageApi.getCurrent();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch analytics dashboard data
 */
export const useAnalyticsDashboard = (period = "30d") =>
  useQuery({
    queryKey: queryKeys.analytics.dashboard(period),
    queryFn: async () => {
      const response = await analyticsApi.getDashboard({ period });
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

// =============================================================================
// TEAM HOOKS
// =============================================================================

/**
 * Fetch team members
 */
export const useTeamMembers = () =>
  useQuery({
    queryKey: queryKeys.team.members,
    queryFn: async () => {
      const response = await teamApi.getMembers();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch team invitations
 */
export const useTeamInvitations = () =>
  useQuery({
    queryKey: queryKeys.team.invitations,
    queryFn: async () => {
      const response = await teamApi.getInvitations();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Invite team member mutation
 */
export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await teamApi.invite(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.invitations });
      toast.success("Invitation sent successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to send invitation"));
    },
  });
};

/**
 * Remove team member mutation
 */
export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberId) => {
      await teamApi.removeMember(memberId);
      return memberId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.members });
      toast.success("Team member removed");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to remove team member"));
    },
  });
};

/**
 * Update member role mutation
 */
export const useUpdateMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, role }) => {
      const response = await teamApi.updateMemberRole(memberId, role);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.members });
      toast.success("Role updated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update role"));
    },
  });
};

// =============================================================================
// ADMIN HOOKS
// =============================================================================

/**
 * Fetch admin dashboard stats
 */
export const useAdminDashboard = () =>
  useQuery({
    queryKey: queryKeys.admin.dashboard,
    queryFn: async () => {
      const response = await adminApi.getDashboard();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch admin users list
 */
export const useAdminUsers = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.admin.users(filters),
    queryFn: async () => {
      const response = await adminApi.getUsers(filters);
      return response.data.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });

/**
 * Fetch single user details (admin)
 */
export const useAdminUser = (id) =>
  useQuery({
    queryKey: queryKeys.admin.userDetail(id),
    queryFn: async () => {
      const response = await adminApi.getUserById(id);
      return response.data.data;
    },
    enabled: !!id,
  });

/**
 * Admin user action mutation (suspend, unsuspend, etc.)
 */
export const useAdminUserAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action, userId }) => {
      let response;
      switch (action) {
        case "suspend":
          response = await adminApi.suspendUser(userId);
          break;
        case "unsuspend":
          response = await adminApi.unsuspendUser(userId);
          break;
        case "delete":
          response = await adminApi.deleteUser(userId);
          break;
        case "resetPassword":
          response = await adminApi.resetUserPassword(userId);
          break;
        default:
          throw new Error("Unknown action");
      }
      return response.data;
    },
    onSuccess: (data, { action }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.users({}) });
      const messages = {
        suspend: "User suspended",
        unsuspend: "User unsuspended",
        delete: "User deleted",
        resetPassword: "Password reset email sent",
      };
      toast.success(messages[action] || "Action completed");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Action failed"));
    },
  });
};

// =============================================================================
// WEBHOOK HOOKS
// =============================================================================

/**
 * Fetch all webhooks
 */
export const useWebhooks = () =>
  useQuery({
    queryKey: queryKeys.webhooks.list(),
    queryFn: async () => {
      const response = await webhooksApi.list();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch webhook details
 */
export const useWebhook = (id) =>
  useQuery({
    queryKey: queryKeys.webhooks.detail(id),
    queryFn: async () => {
      const response = await webhooksApi.get(id);
      return response.data.data;
    },
    enabled: !!id,
  });

/**
 * Fetch webhook logs
 */
export const useWebhookLogs = (id) =>
  useQuery({
    queryKey: queryKeys.webhooks.logs(id),
    queryFn: async () => {
      const response = await webhooksApi.getLogs(id);
      return response.data.data;
    },
    enabled: !!id,
  });

/**
 * Create webhook mutation
 */
export const useCreateWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await webhooksApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all });
      toast.success("Webhook created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create webhook"));
    },
  });
};

/**
 * Update webhook mutation
 */
export const useUpdateWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await webhooksApi.update(id, data);
      return response.data.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all });
      toast.success("Webhook updated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update webhook"));
    },
  });
};

/**
 * Delete webhook mutation
 */
export const useDeleteWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await webhooksApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.webhooks.all });
      toast.success("Webhook deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete webhook"));
    },
  });
};

// =============================================================================
// API KEY HOOKS
// =============================================================================

/**
 * Fetch all API keys
 */
export const useApiKeys = () =>
  useQuery({
    queryKey: queryKeys.apiKeys.list(),
    queryFn: async () => {
      const response = await apiKeysApi.list();
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Create API key mutation
 */
export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiKeysApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      toast.success("API key created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create API key"));
    },
  });
};

/**
 * Delete API key mutation
 */
export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await apiKeysApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      toast.success("API key deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete API key"));
    },
  });
};

// =============================================================================
// SIGNING LINK HOOKS
// =============================================================================

/**
 * Fetch signing links
 */
export const useSigningLinks = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.signingLinks.list(filters),
    queryFn: async () => {
      const response = await signingLinksApi.list(filters);
      return response.data.data;
    },
    staleTime: 30 * 1000, // 30 seconds
  });

/**
 * Create signing link mutation
 */
export const useCreateSigningLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await signingLinksApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.signingLinks.all });
      toast.success("Signing link created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create signing link"));
    },
  });
};

/**
 * Delete signing link mutation
 */
export const useDeleteSigningLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await signingLinksApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.signingLinks.all });
      toast.success("Signing link deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete signing link"));
    },
  });
};

// =============================================================================
// CLAUSE HOOKS
// =============================================================================

/**
 * Fetch clauses
 */
export const useClauses = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.clauses.list(filters),
    queryFn: async () => {
      const response = await clauseApi.list(filters);
      return response.data.data;
    },
    staleTime: 60 * 1000, // 1 minute
  });

/**
 * Fetch single clause
 */
export const useClause = (id) =>
  useQuery({
    queryKey: queryKeys.clauses.detail(id),
    queryFn: async () => {
      const response = await clauseApi.getById(id);
      return response.data.data;
    },
    enabled: !!id,
  });

/**
 * Create clause mutation
 */
export const useCreateClause = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await clauseApi.create(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clauses.all });
      toast.success("Clause created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create clause"));
    },
  });
};

/**
 * Update clause mutation
 */
export const useUpdateClause = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await clauseApi.update(id, data);
      return response.data.data;
    },
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clauses.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.clauses.all });
      toast.success("Clause updated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update clause"));
    },
  });
};

/**
 * Delete clause mutation
 */
export const useDeleteClause = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await clauseApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clauses.all });
      toast.success("Clause deleted successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete clause"));
    },
  });
};

// =============================================================================
// DASHBOARD HOOKS
// =============================================================================

/**
 * Fetch dashboard data (stats, recent envelopes, activities)
 */
export const useDashboardData = () =>
  useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const [statsRes, envelopesRes, activitiesRes] = await Promise.all([
        envelopeApi.getStats(),
        envelopeApi.list({
          limit: 5,
          sortBy: "updated_at",
          sortOrder: "desc",
        }),
        usageApi
          .getAllActivity(1, 5)
          .catch(() => ({ data: { activities: [] } })),
      ]);

      const activityData = activitiesRes.data?.data || activitiesRes.data;

      return {
        stats: statsRes.data.data,
        recentEnvelopes: envelopesRes.data.data,
        recentActivities: activityData?.activities || [],
      };
    },
    staleTime: 30 * 1000, // 30 seconds
  });

/**
 * Fetch templates for template picker (my + public)
 */
export const useTemplatesForPicker = () =>
  useQuery({
    queryKey: ["templates", "picker"],
    queryFn: async () => {
      const [myTemplates, publicTemplates] = await Promise.all([
        templateApi.list({ limit: 10 }),
        templateApi.listPublic({ limit: 10 }),
      ]);

      const allTemplates = [...(myTemplates.data.data || [])];
      const publicOnes = publicTemplates.data.data || [];

      publicOnes.forEach((tmpl) => {
        if (!allTemplates.find((at) => at.id === tmpl.id)) {
          allTemplates.push(tmpl);
        }
      });

      return allTemplates;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: false, // Only fetch when needed
  });

// =============================================================================
// PUBLIC TEMPLATES HOOKS
// =============================================================================

/**
 * Fetch public templates
 */
export const usePublicTemplates = (filters = {}) =>
  useQuery({
    queryKey: ["templates", "public", filters],
    queryFn: async () => {
      const response = await templateApi.listPublic(filters);
      return response.data.data;
    },
    staleTime: 60 * 1000,
  });

// =============================================================================
// ORGANIZATION/TEAM HOOKS
// =============================================================================

/**
 * Fetch organisation with members and invitations
 */
export const useOrganization = () =>
  useQuery({
    queryKey: ["organisation"],
    queryFn: async () => {
      const response = await teamApi.getOrganization();
      return response.data?.data || null;
    },
    staleTime: 60 * 1000,
  });

/**
 * Create organisation mutation
 */
export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await teamApi.createOrganization(data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation"] });
      toast.success("Organization created successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create organisation"));
    },
  });
};

/**
 * Cancel invitation mutation
 */
export const useCancelInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId) => {
      await teamApi.cancelInvitation(invitationId);
      return invitationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisation"] });
      toast.success("Invitation cancelled");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to cancel invitation"));
    },
  });
};

// =============================================================================
// TAG HOOKS (Extended)
// =============================================================================

/**
 * Update tag mutation
 */
export const useUpdateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await tagApi.update(id, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.all });
      toast.success("Tag updated successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update tag"));
    },
  });
};

// =============================================================================
// API KEY HOOKS
// =============================================================================

/**
 * Fetch API keys
 */
export const useApiKeysList = () =>
  useQuery({
    queryKey: queryKeys.apiKeys.list(),
    queryFn: async () => {
      const response = await apiKeysApi.list();
      return response.data?.keys || response.data?.data?.keys || [];
    },
    staleTime: 60 * 1000,
  });

/**
 * Create API key mutation
 */
export const useCreateApiKeyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiKeysApi.create(data);
      return response.data?.apiKey || response.data?.data?.apiKey;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to create API key"));
    },
  });
};

/**
 * Revoke API key mutation
 */
export const useRevokeApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await apiKeysApi.revoke(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      toast.success("API key revoked");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to revoke API key"));
    },
  });
};

/**
 * Reactivate API key mutation
 */
export const useReactivateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiKeysApi.reactivate(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      toast.success("API key reactivated");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to reactivate API key"));
    },
  });
};

/**
 * Delete API key mutation
 */
export const useDeleteApiKeyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await apiKeysApi.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
      toast.success("API key deleted");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to delete API key"));
    },
  });
};

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Prefetch data for navigation
 */
export const usePrefetch = () => {
  const queryClient = useQueryClient();

  return {
    prefetchEnvelopes: (filters) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.envelopes.list(filters),
        queryFn: () => envelopeApi.list(filters).then((r) => r.data),
      });
    },
    prefetchTemplates: (filters) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.templates.list(filters),
        queryFn: () => templateApi.list(filters).then((r) => r.data),
      });
    },
    prefetchEnvelope: (id) => {
      queryClient.prefetchQuery({
        queryKey: queryKeys.envelopes.detail(id),
        queryFn: () => envelopeApi.get(id).then((r) => r.data.data),
      });
    },
  };
};
